import { Box, Button, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AlertCircle from "../../../../../../assets/images/svgs/alert-circle.svg";
import AlertTriangle from "../../../../../../assets/images/svgs/alert-triangle.svg";
import { APPS } from "../../../../../../constants/AppsConstants.ts";
import LinkConstants from "../../../../../../constants/LinkConstants.ts";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import iframeStore from "../../../../../IframeNew/iframe-store.tsx";
import styles from "./TextToImageError.module.scss";

type Props = {
	className?: string;
	anchorRef?: React.RefObject<HTMLDivElement>;
	containerRef?: React.MutableRefObject<HTMLDivElement | null>;
};

const TextToImageError = ({ anchorRef, containerRef, className }: Props) => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "playground.textToImage.errors" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const [open, setOpened] = useState(false);
	const [errorExist, setErrorExist] = useState(false);
	const [isAnchorVisible, setIsAnchorVisible] = useState(false);
	const [message, setMessage] = useState("");
	const [boldMessage, setBoldMessage] = useState("");
	const [errorStyle, setErrorStyle] = useState<"error" | "warning">("error");
	const [position, setPosition] = useState({ top: 0, left: 0 });
	const isNewTg = playgroundStore.selectedConfig == APPS.TG_IMAGE_GENERATION;

	const calculateOffsetY = () => {
		if (errorStyle === "error" || textToImageStore.IpSignalWarning) {
			return textToImageStore.IpSignalWarning ? 10 : 60;
		}
		return -45;
	};

	const updatePosition = () => {
		const rect = anchorRef?.current?.getBoundingClientRect();
		if (rect) {
			setPosition({
				top: rect.top + calculateOffsetY(),
				left: rect.right,
			});
		}
	};

	const handleScroll = () => {
		if (!open) return;

		const rect = anchorRef?.current?.getBoundingClientRect();
		const containerRect = containerRef?.current?.getBoundingClientRect();

		if (!rect || !containerRect) return;

		const visibilityOffsetTop = errorStyle == "error" ? 100 : 20;
		const visibilityOffsetBottom = errorStyle == "error" ? 0 : -200;

		const isVisible =
			rect.top + visibilityOffsetTop >= containerRect.top &&
			rect.bottom + visibilityOffsetBottom <= containerRect.bottom;

		setIsAnchorVisible(isVisible);

		if (isVisible) {
			updatePosition();
		}
	};

	useEffect(() => {
		const el = containerRef?.current;
		if (!el || !open) return;

		el.addEventListener("scroll", handleScroll);

		return () => {
			el.removeEventListener("scroll", handleScroll);
		};
	}, [open, errorStyle]);

	useEffect(() => {
		if (!anchorRef?.current || !containerRef?.current || !errorExist) return;

		const anchorRect = anchorRef.current.getBoundingClientRect();
		const containerRect = containerRef.current.getBoundingClientRect();

		const isVisible = anchorRect.top >= containerRect.top && anchorRect.bottom <= containerRect.bottom;

		if (!isVisible) {
			containerRef.current.scroll({
				top: (anchorRef.current.offsetTop ?? 0) - 100,
				behavior: "smooth",
			});
		}

		const timeoutId = setTimeout(
			() => {
				updatePosition();
				setOpened(true);
				setIsAnchorVisible(true);
			},
			isVisible ? 0 : 500,
		);

		return () => clearTimeout(timeoutId);
	}, [errorExist]);

	useEffect(() => {
		const isErrorMessageExists = i18n.exists(
			`playground.textToImage.errors.${textToImageStore.errors?.generate?.message}`,
		);
		if (isErrorMessageExists && isNewTg) {
			setErrorStyle("error");
			setErrorExist(true);
			setMessage(t(`${textToImageStore.errors?.generate?.message}`));
		} else if (textToImageStore.errors?.generate?.status == 422) {
			setErrorExist(true);
			setBoldMessage(t("contentModerationErrorBoldMessage"));
			setMessage(t("contentModerationErrorMessage"));
			setErrorStyle("warning");
		} else if (textToImageStore.IpSignalWarning && !iframeStore.isIframe() && isNewTg) {
			setErrorExist(true);
			setBoldMessage(t("ipSignalWarningBoldMessage"));
			setMessage(t("ipSignalWarningMessage"));
			setErrorStyle("warning");
		} else {
			setErrorExist(false);
			setBoldMessage("");
			setMessage("");
			setOpened(false);
		}
	}, [textToImageStore.errors.generate, textToImageStore.IpSignalWarning]);

	const handleOnDismiss = () => {
		setOpened(false);
		textToImageStore.errors = {};
	};

	const getLearnMoreLink = () => {
		switch (message) {
			case t("ipSignalWarningMessage"):
				return LinkConstants.IP_SIGNAL_BRIA_API_DOC;
			case t("contentModerationErrorMessage"):
				return LinkConstants.CONTENT_MODERATION_BRIA_API_DOC;
			default:
				return LinkConstants.PROMPT_MODERATION_BRIA_API_DOC;
		}
	};

	return createPortal(
		<>
			{open &&
				isAnchorVisible &&
				(errorStyle == "error" ? (
					<>
						<Box
							className={`${styles.errorMessageContainer} ${className || ""}`}
							sx={{
								top: position.top,
								left: position.left,
							}}
						>
							<Box className={styles.errorSection}>
								<img src={AlertCircle} />
								<Typography className={styles.errorMessage}>{message}</Typography>
							</Box>
							<Box className={styles.errorButtons}>
								<Button
									component={"a"}
									className={styles.errorLearnMoreButton}
									href={getLearnMoreLink()}
									target={"_blank"}
									rel={"noopener noreferrer"}
								>
									{t("learnMoreButton")}
								</Button>
								<Button className={styles.errorDismissButton} onClick={handleOnDismiss}>
									{t("dismissButton")}
								</Button>
							</Box>
						</Box>
					</>
				) : (
					<Box
						className={styles.warningMessageContainer}
						sx={{
							top: position.top,
							left: position.left,
						}}
					>
						{
							<>
								<Box className={styles.errorSection}>
									<img src={AlertTriangle} />
									<Box>
										<Typography className={styles.warningBoldMessage}>{boldMessage}</Typography>
										<Typography className={styles.warningMessage}>{message}</Typography>
									</Box>
								</Box>
								<Box className={styles.errorButtons}>
									<Button
										component={"a"}
										className={styles.errorLearnMoreButton}
										href={getLearnMoreLink()}
										target={"_blank"}
										rel={"noopener noreferrer"}
									>
										{t("learnMoreButton")}
									</Button>
									<Button className={styles.errorDismissButton} onClick={handleOnDismiss}>
										{t("dismissButton")}
									</Button>
								</Box>
							</>
						}
					</Box>
				))}
		</>,
		document.body,
	);
};

export default observer(TextToImageError);
